import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'
import { createPortal } from 'react-dom'

/**
 * Shadow DOM isolation costs us the usual `createPortal(node, document.body)`
 * escape hatch: portalling to the body drops content back into Webflow's
 * cascade, unstyled.
 *
 * Every mount therefore gets its own portal host inside its shadow root, and
 * overlay components portal there instead. Styles still apply, and the overlay
 * escapes any `overflow: hidden` or transform on intermediate component nodes.
 */
const PortalHostContext = createContext<HTMLElement | null>(null)

export const PortalHostProvider = PortalHostContext.Provider

/** The portal host element for the current mount, if there is one. */
export function usePortalHost(): HTMLElement | null {
  return useContext(PortalHostContext)
}

/**
 * Render children into this mount's portal host. Falls back to rendering in
 * place when no host exists (e.g. a component rendered outside a mount in a
 * unit test), so it is always safe to use.
 */
export function Portal({ children }: { children: ReactNode }): ReactNode {
  const host = usePortalHost()
  return host ? createPortal(children, host) : children
}
